import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { ReimbursementTransactionService } from 'us-web-services';
import { Reimbursement } from 'us-web-services/dist/types';
import { formatDateToString, DateFormat, parseDateInput } from 'us-react-components/dist/utils/date';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { ReimbursementTable } from './components/ReimbursementTable';
import { ReimbursementFilters } from './components/ReimbursementFilters';

export const Reimbursements = observer((): JSX.Element => {
  document.title = 'UrbanSitter - Reimbursements';
  const { stores } = useDependencies();
  const { t } = useTranslation();

  const [reimbursements, setReimbursements] = useState<Reimbursement[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [meta, setMeta] = useState<{
    page?: number;
    pageCount?: number;
    size?: number;
  }>({});

  const formatApiDate = (date: Date): string =>
    `${formatDateToString(date, DateFormat.API_DATE_FORMAT)} 00:00:00`;

  const fetchReimbursements = useCallback(async (page: number = 1, limit: number = 10) => {
    if (!stores.user.user) return;

    setLoading(true);
    try {

      const params: Record<string, unknown> = {
        userId: stores.user.user.id,
        parentId: stores.user.user.id,
        page,
        limit,
        sort: 'startDate',
        'startDate.dir': 'desc',
      };

      if (selectedYear !== null) {
        const startDate = new Date(selectedYear, selectedMonth !== null ? selectedMonth - 1 : 0, 1);
        const endDate = selectedMonth !== null
          ? new Date(selectedYear, selectedMonth, 0)
          : new Date(selectedYear, 11, 31);

        params.start = formatApiDate(startDate);
        params.end = formatApiDate(endDate);
      }

      const response = await ReimbursementTransactionService.getByFilter({
        params,
      });

      setReimbursements(response.data.data as Reimbursement[]);
      setMeta(response.data.meta);
    } catch (error) {
      console.error('Error fetching reimbursements:', error);
    } finally {
      setLoading(false);
    }
  }, [stores.user.user, selectedYear, selectedMonth]);

  const exportToCsv = useCallback(async () => {
    if (!stores.user.user || selectedYear === null) return;

    setExporting(true);
    try {
      const startDate = new Date(selectedYear, selectedMonth !== null ? selectedMonth - 1 : 0, 1);
      const endDate = selectedMonth !== null
        ? new Date(selectedYear, selectedMonth, 0)
        : new Date(selectedYear, 11, 31);

      const response = await ReimbursementTransactionService.getByFilter({
        params: {
          userId: stores.user.user.id,
          parentId: stores.user.user.id,
          start: formatApiDate(startDate),
          end: formatApiDate(endDate),
          page: 1,
          limit: 2000,
          sort: 'startDate',
          'startDate.dir': 'desc',
        },
      });

      const headers = [
        t('pages.reimbursements.table.voucherDeducted'),
        t('pages.reimbursements.table.servicesProvided'),
        t('pages.reimbursements.table.qualifiedDependent'),
        t('pages.reimbursements.table.startDate'),
        t('pages.reimbursements.table.endDate'),
        t('pages.reimbursements.table.amountReimbursed'),
      ];

      const rows = (response.data.data as Reimbursement[]).map((reimbursement) => {
        let qualifiedDependentText = '';
        if (reimbursement.qualifiedDependent === true) {
          qualifiedDependentText = t('pages.reimbursements.values.true');
        } else if (reimbursement.qualifiedDependent === false) {
          qualifiedDependentText = t('pages.reimbursements.values.false');
        }

        return [
          reimbursement.voucherCode || '',
          reimbursement.careTypeCategory || '',
          qualifiedDependentText,
          reimbursement.startDate ? formatDateToString(parseDateInput(reimbursement.startDate), DateFormat.DATE_LONG_FORMAT_SLASHES) : '',
          reimbursement.endDate ? formatDateToString(parseDateInput(reimbursement.endDate), DateFormat.DATE_LONG_FORMAT_SLASHES) : '',
          reimbursement.amountToDeduct ? `$${reimbursement.amountToDeduct.toFixed(2)}` : '',
        ];
      });

      const escapeCsvCell = (cell: string): string => `"${cell.replace(/"/g, '""')}"`;
      const csvContent = [headers, ...rows]
        .map((row: string[]) => row.map((cell: string) => escapeCsvCell(cell)).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${selectedYear} UrbanSitter Reimbursements.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting reimbursements:', error);
    } finally {
      setExporting(false);
    }
  }, [stores.user.user, selectedYear, selectedMonth]);

  useEffect(() => {
    fetchReimbursements();
  }, [fetchReimbursements]);

  const formatCurrency = (amount?: number): string => {
    if (amount === undefined || amount === null) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date?: Date): string => {
    if (!date) return '';

    return formatDateToString(parseDateInput(date), DateFormat.DATE_LONG_FORMAT_SLASHES);
  };

  const formatQualifiedDependent = (value?: boolean): string => {
    if (value === true) return t('pages.reimbursements.values.true');
    if (value === false) return t('pages.reimbursements.values.false');
    return '';
  };

  const currentDateString = formatDateToString(new Date(), DateFormat.DATE_FULL_FORMAT);

  const profileName = `${stores.user.user?.firstName || ''} ${stores.user.user?.lastName || ''}`.trim();

  return (
    <div className='container py-6'>
      <h1 className='text-2xl font-semibold mb-6'>{t('pages.reimbursements.header')}</h1>

      <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6'>
        <ReimbursementFilters
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          onYearChange={setSelectedYear}
          onMonthChange={setSelectedMonth}
        />
        <Button
          buttonType={ButtonType.primary}
          onClick={exportToCsv}
          disabled={exporting || loading || selectedYear === null}
        >
          {exporting ? t('pages.reimbursements.buttons.exporting') : t('pages.reimbursements.buttons.export')}
        </Button>
      </div>

      {loading && (
        <div className='text-center py-8'>
          <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600' />
          <p className='mt-2'>{t('pages.reimbursements.messages.loading')}</p>
        </div>
      )}

      {!loading && reimbursements.length === 0 && (
        <div className='text-center py-8'>
          <h2 className='text-lg font-medium text-gray-600'>{t('pages.reimbursements.messages.noResults')}</h2>
        </div>
      )}

      {!loading && reimbursements.length > 0 && (
        <>
          {exporting && (
            <div className='text-center py-4'>
              <div className='inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2' />
              <span className='text-sm text-gray-600'>{t('pages.reimbursements.messages.exportingData')}</span>
            </div>
          )}
          <ReimbursementTable
            reimbursements={reimbursements}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            formatQualifiedDependent={formatQualifiedDependent}
          />

          {meta && meta.pageCount && meta.pageCount > 1 && (
            <div className='flex justify-center mt-6 space-x-2'>
              <Button
                buttonType={ButtonType.secondary}
                onClick={(): void => {
                  fetchReimbursements((meta.page || 1) - 1);
                }}
                disabled={(meta.page || 1) <= 1}
              >
                {t('common.previous')}
              </Button>
              <span className='py-2 px-4'>
                {t('pages.reimbursements.messages.page', { current: meta.page || 1, total: meta.pageCount })}
              </span>
              <Button
                buttonType={ButtonType.secondary}
                onClick={(): void => {
                  fetchReimbursements((meta.page || 1) + 1);
                }}
                disabled={(meta.page || 1) >= (meta.pageCount || 1)}
              >
                {t('common.next')}
              </Button>
            </div>
          )}

          <div className='text-center text-sm text-gray-600 mt-8'>
            {currentDateString} - {profileName}
          </div>
        </>
      )}
    </div>
  );
});
